import { useAuth0 } from '@auth0/auth0-react';
import { LogOut } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import {
  focusMenuItem,
  menuButtonStart,
  onMenuKeyDown,
  type MenuStart,
} from '@/components/ui/menu-keys';
import { useDismiss } from '@/components/ui/use-dismiss';
import { env } from '@/lib/env';
import { meInitials, useMe, type MeData } from '@/lib/me';

/** Nombre legible del usuario: nombre y apellido, el nombre del token o el correo. */
function displayName(me: MeData | undefined): string {
  if (me === undefined) {
    return '';
  }
  const full = [me.first_name, me.last_name].filter(Boolean).join(' ').trim();
  return full || me.name || me.email || '';
}

/**
 * Menú de la cuenta en el avatar: quién está conectado y "Cerrar sesión", que pide
 * confirmación con el diálogo del sistema (UX-FB-01), nunca un confirm del navegador.
 * Escape o un clic afuera lo cierran (Escape devuelve el foco al avatar); Tab lo cierra
 * y sigue, como el patrón de menú de WAI-ARIA.
 */
export function UserMenu(): ReactElement {
  const me = useMe();
  const { logout } = useAuth0();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<MenuStart>('first');

  const data = me.data?.data;
  const initials = data ? meInitials(data) : '··';
  const name = displayName(data);
  const email = data?.email ?? null;

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapRef], buttonRef);

  useEffect(() => {
    if (open) {
      focusMenuItem(menuRef.current, startRef.current);
    }
  }, [open]);

  return (
    <div className="user-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="avatar-user"
        aria-label={name ? `Menú de la cuenta de ${name}` : 'Menú de la cuenta'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={name || 'Tu cuenta'}
        onClick={() => {
          startRef.current = 'first';
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          const start = menuButtonStart(e);
          if (start !== null) {
            startRef.current = start;
            if (open) {
              focusMenuItem(menuRef.current, start);
            } else {
              setOpen(true);
            }
          }
        }}
      >
        {initials}
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          className="pop-menu"
          role="menu"
          tabIndex={-1}
          aria-label="Menú de la cuenta"
          onKeyDown={(e) => onMenuKeyDown(e, close)}
        >
          {(name || email) && (
            <div className="user-menu-head" role="presentation">
              {name && <strong>{name}</strong>}
              {email && name !== email && <span>{email}</span>}
            </div>
          )}
          <button
            type="button"
            role="menuitem"
            className="pop-item"
            onClick={() => {
              // El foco vuelve al avatar antes de abrir el diálogo: así el diálogo lo
              // devuelve ahí al cerrarse, no al inicio de la página.
              buttonRef.current?.focus();
              setOpen(false);
              setConfirming(true);
            }}
          >
            <LogOut size={16} aria-hidden="true" />
            Cerrar sesión
          </button>
        </div>
      )}
      <ConfirmModal
        open={confirming}
        title="¿Cerrar la sesión?"
        message="Para volver a usar el QMS tendrás que iniciar sesión de nuevo."
        confirmLabel="Cerrar sesión"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void logout({ logoutParams: { returnTo: env.VITE_AUTH0_LOGOUT_RETURN_TO } });
        }}
      />
    </div>
  );
}
